import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import api from "../api/API";
import AppLayout from "../layout/AppLayout";
import { useAuth } from "../hooks/useAuth";
import { useWorkspace } from "../context/WorkspaceContext";
import { usePlan } from "../context/PlanContext";

import DailyHeader from "../components/dashboardTemp/DailyHeader";
import StatusCard from "../components/dashboardTemp/StatusCard";
import DailyFocusCard from "../components/dashboardTemp/DailyFocusCard";
import HabitsCard from "../components/dashboardTemp/HabitsCard";
import MyTodayTasksCard from "../components/dashboardTemp/MyTodayTasksCard";
import LembretesCard from "../components/dashboardTemp/LembretesCard";
import PlanejamentoDiaModal from "./PlanejamentoDiaModal";

import DiaExecucaoHeader from "../components/DiaExecucaoHeader";
import DashboardCommandBar from "../components/DashboardCommandBar";

import { addDays, isSameDay, toYMD } from "../utils/DateUtils";

import { TASK_PRIORITY } from "../utils/taskPriority";


function normalizeText(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function findProjetoIdBySlug(projects, slug) {
  if (!slug) return null;
  const needle = normalizeText(slug);

  // tenta por igualdade
  let p = projects.find(x => normalizeText(x.descricao || x.nome || x.titulo).replace(/\s+/g, "") === needle.replace(/\s+/g, ""));
  if (p) return p.id;

  // tenta contains
  p = projects.find(x => normalizeText(x.descricao || x.nome || x.titulo).includes(needle));
  return p ? p.id : null;
}

const DashboardPage = () => {
  const { user } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const { temRecurso } = usePlan();
  const temHabitos = temRecurso("habitos");
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [membros, setMembros] = useState([]);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [diaAtual, setDiaAtual] = useState(null);
  const [showPlanejamento, setShowPlanejamento] = useState(false);
  const [tasksRefreshKey, setTasksRefreshKey] = useState(0);
  const commandBarRef = useRef(null);

  const [selectedDiaTarefaId, setSelectedDiaTarefaId] = useState(null);

  const ymd = useMemo(() => toYMD(selectedDate), [selectedDate]);
  const isTodayMemo = useMemo(() => isSameDay(selectedDate, new Date()), [selectedDate]);

  // carrega projetos uma vez (precisa existir endpoint /projects)
  useEffect(() => {
    api.get("/projects")
      .then(res => setProjects(res.data || []))
      .catch(() => setProjects([]));
  }, []);

  // Membros do workspace - usados pelo Command Palette pra resolver @pessoa
  // de verdade (antes, @algo era so ignorado e a tarefa sempre ia pro
  // proprio usuario).
  useEffect(() => {
    if (!selectedWorkspaceId) {
      setMembros([]);
      return;
    }
    api.get(`/workspaces/${selectedWorkspaceId}/members`)
      .then((res) => setMembros(res.data?.records || res.data || []))
      .catch(() => setMembros([]));
  }, [selectedWorkspaceId]);

  useEffect(() => {
    carregarDiaPorData(ymd);
    setSelectedDiaTarefaId(null);
  }, [ymd]);

  async function carregarDiaPorData(dataYMD) {
    try {
      const res = await api.get(`/dias/por-data/${dataYMD}`);
      setDiaAtual(res.data);
    } catch (err) {
      console.error("Erro ao carregar/criar dia por data:", err);
      setDiaAtual(null);
    }
  }

  function prevDay() { setSelectedDate((d) => addDays(d, -1)); }
  function nextDay() { setSelectedDate((d) => addDays(d, 1)); }
  function goToday() { setSelectedDate(new Date()); }

  function abrirPlanejamento() {
    if (!diaAtual?.id) return;
    setShowPlanejamento(true);
  }

  // Hotkeys
  useEffect(() => {
    function isTypingTarget(el) {
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      return tag === "input" || tag === "textarea" || el.isContentEditable;
    }

    async function handleKeyDown(e) {
      if (isTypingTarget(document.activeElement)) {
        if (!(e.ctrlKey && e.key.toLowerCase() === "k")) return;
      }

      if (e.ctrlKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        commandBarRef.current?.focus();
        return;
      }

      if (e.key.toLowerCase() === "n") {
        e.preventDefault();
        commandBarRef.current?.focus();
        return;
      }

      if (e.key === "ArrowLeft") { e.preventDefault(); prevDay(); return; }
      if (e.key === "ArrowRight") { e.preventDefault(); nextDay(); return; }

      if (e.code === "Space") {
        if (!selectedDiaTarefaId) return;
        e.preventDefault();
        try {
          await api.put(`/dias/tarefas/${selectedDiaTarefaId}`, {});
          // Se precisar, você força refresh aqui
          carregarDiaPorData(toYMD(selectedDate));
        } catch (err) {
          console.error("Erro ao alternar tarefa selecionada:", err);
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedDiaTarefaId, selectedDate]);

  // Submit do command palette
  async function handleSubmitQuickTask(parsed, rawText, responsavel) {
    try {
      // 1) resolve projeto
      const id_projeto = findProjetoIdBySlug(projects, parsed.projetoSlug);

      // 2) responsavel ja vem resolvido pela DashboardCommandBar (contra os
      // membros reais do workspace) - ela so chama onSubmit quando o @
      // digitado bateu em exatamente uma pessoa, ou nao tinha @ nenhum
      // (default: voce).
      const id_usuario_responsavel = responsavel?.id || user?.id || null;

      // 3) payload real compatível com ttarefas
      const payload = {
        fields: {
          descricao: parsed.descricao,
          detalhe: rawText,                 // opcional, bom pra auditoria
          id_projeto: id_projeto,           // pode ser null
          // Sem isto, uma tarefa sem projeto nasce com id_workspace NULL e
          // desaparece de todas as listas, que filtram por workspace.
          id_workspace: selectedWorkspaceId || null,
          prioridade: parsed.prioridade ?? TASK_PRIORITY.MEDIUM,
          data_inicio: parsed.dataInicio || null,
          vencimento: parsed.vencimento || null,
          concluido: false,
          ativo: true,
          tipo: 0,                          // ajuste conforme seu sistema
          id_usuario_responsavel: id_usuario_responsavel,
          id_dia: diaAtual?.id || null      // 👈 amarra no dia (ótimo pra execução)
        }
      };

      // Observação:
      // id_usuario idealmente o backend seta via token (recomendado).
      // Mas se seu createItem não seta, você pode mandar:
      // payload.id_usuario = user.id;

      await api.post("/tasks", payload);

      // refresh do quadro
      carregarDiaPorData(toYMD(selectedDate));
      setTasksRefreshKey((current) => current + 1);
    } catch (err) {
      console.error("Erro ao criar tarefa via command palette:", err);
      alert("Falha ao criar tarefa. Verifique o endpoint POST /tasks.");
    }
  }

  return (
      <Container fluid className="p-3 p-md-4">
        <DashboardCommandBar
          ref={commandBarRef}
          nomeUsuario={user?.nome || user?.name || user?.email?.split("@")[0] || "voce"}
          membros={membros}
          currentUserId={user?.id}
          onSubmit={handleSubmitQuickTask}
          onPlanejar={abrirPlanejamento}
          onNovaNota={() => navigate("/notes")}
          onAbrirOkrs={() => navigate("/okrs")}
          onVerTime={() => navigate("/dashboard-gestor")}
        />

        <DiaExecucaoHeader
          selectedDate={selectedDate}
          isToday={isTodayMemo}
          onPrev={prevDay}
          onNext={nextDay}
          onToday={goToday}
          onAbrirPlanejamento={abrirPlanejamento}
          onRevisao={() => alert("Revisão do dia (em breve)")}
          onVitoria={() => alert("Registrar vitória (em breve)")}
        />

        <DailyHeader date={selectedDate} />
        <StatusCard />

        <Row className="mt-4">
          <Col md={7}>
            <DailyFocusCard
              dia={diaAtual}
              selectedDiaTarefaId={selectedDiaTarefaId}
              setSelectedDiaTarefaId={setSelectedDiaTarefaId}
              onAbrirPlanejamento={abrirPlanejamento}
            />
            <MyTodayTasksCard selectedDate={selectedDate} refreshKey={tasksRefreshKey} />
          </Col>
          <Col md={5}>
            <LembretesCard />
            {temHabitos && <HabitsCard dia={diaAtual} selectedDate={selectedDate} />}
          </Col>
        </Row>

        <PlanejamentoDiaModal
          show={showPlanejamento}
          onHide={() => setShowPlanejamento(false)}
          dia={diaAtual}
          onSaved={() => {
            setShowPlanejamento(false);
            carregarDiaPorData(toYMD(selectedDate));
          }}
        />

      </Container>
  );
};

export default DashboardPage;
